// File: src/pages/reports/DamageStockReport.jsx
import React from "react";
import {
  Card,
  CardBody,
  Input,
  Row,
  Modal,
  Col,
  ModalHeader,
  ModalBody,
  Badge,
  Label,
  Button,
  Spinner,
  Table,
  UncontrolledButtonDropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
} from "reactstrap";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {
  FaArrowAltCircleLeft,
  FaArrowAltCircleRight,
  FaFilter,
} from "react-icons/fa";
import {
  BsFillArrowDownSquareFill,
  BsFillArrowUpSquareFill,
} from "react-icons/bs";
import { ImDownload } from "react-icons/im";
import { IoMdRemoveCircleOutline } from "react-icons/io";

import UserContext from "../import/context/Context";
import { _Get } from "../import/ApiEndPoint/ApiCalling";
import { Create_Warehouse_List } from "../import/ApiEndPoint/Api";
import { CheckPermission } from "../import/CheckPermission";
import SuperAdminUI from "../import/SuperAdminUI";

// ---------------------------------------------
// Helpers
// ---------------------------------------------
const SelectedColums = []; // temp bucket used by the modal to add to "visible" set

const safe = (v) => (v === undefined || v === null ? "" : v);
const toDateOnly = (iso) => {
  const d = safe(iso);
  if (!d) return "";
  try {
    return String(d).split("T")[0];
  } catch {
    return d;
  }
};

const getValueByPath = (obj, path) =>
  path
    .split(".")
    .reduce(
      (acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined),
      obj
    );

// ---------------------------------------------
// Main Component
// ---------------------------------------------
class DamageStockReport extends React.Component {
  static contextType = UserContext;

  constructor(props) {
    super(props);
    this.state = {
      // ui
      Loading: false,
      MasterShow: false,
      isOpen: false, // download hover dropdown
      modal: false, // column chooser
      Arrindex: -1,

      // data
      rowData: [], // filtered (by date) dataset
      rowAllData: [], // full dataset
      startDate: "",
      EndDate: "",

      // permissions
      InsiderPermissions: {},

      // search + sort + pagination
      searchText: "",
      paginationPageSize: 15,
      currentPage: 1,
      sortConfig: { key: "UID", direction: "asc" },

      // columns config
      AllcolumnDefs: [],
      SelectedcolumnDefs: [],
    };
  }

  // ---------------------------------------------
  // Column setup (value accessors, headers)
  // ---------------------------------------------
  baseColumns = [
    {
      id: "UID",
      headerName: "UID",
      width: 80,
      getValue: (_row, _idx, absoluteIndex) => absoluteIndex + 1,
      sortable: false,
    },
    {
      id: "product",
      headerName: "Product",
      getValue: (row) => safe(row?.damage?.productId?.Product_Title),
    },
    {
      id: "hsn",
      headerName: "HSN",
      width: 130,
      getValue: (row) => safe(row?.damage?.productId?.HSN_Code),
    },
    {
      id: "taxable",
      headerName: "Taxable Amount",
      width: 175,
      getValue: (row) => Number(row?.damage?.productId?.Product_MRP || 0),
    },
    {
      id: "gstamount",
      headerName: "GST Amount",
      width: 120,
      getValue: (row) => {
        const mrp = Number(row?.damage?.productId?.Product_MRP || 0);
        const rate = Number(row?.damage?.productId?.GSTRate || 0);
        const amt = mrp * (rate / 100);
        return Number.isFinite(amt) ? +amt.toFixed(2) : 0;
      },
    },
    {
      id: "warehouse",
      headerName: "Warehouse Name",
      width: 270,
      getValue: (row) => safe(row?.warehouseName),
    },
    {
      id: "date",
      headerName: "Date Reported",
      width: 140,
      getValue: (row) => toDateOnly(row?.createdAt || row?.updatedAt),
    },
  ];

  // ---------------------------------------------
  // Lifecycle
  // ---------------------------------------------
  async componentDidMount() {
    const userInfo = JSON.parse(localStorage.getItem("userData"));
    if (userInfo?.rolename?.roleName === "MASTER") {
      this.setState({ MasterShow: true });
    }

    const InsidePermissions = CheckPermission("Damage Reports");
    this.setState({ InsiderPermissions: InsidePermissions });

    // columns init (from localStorage or defaults)
    const saved = JSON.parse(
      localStorage.getItem("closingStockReport") || "null"
    );
    if (Array.isArray(saved) && saved.length) {
      // saved is an array of { headerName } from old grid — map to our ids if possible
      // Fallback: use baseColumns order
      const byHeader = Object.fromEntries(
        this.baseColumns.map((c) => [c.headerName, c])
      );
      const selected = saved
        .map((s) => byHeader[s?.headerName])
        .filter(Boolean);
      this.setState({
        AllcolumnDefs: this.baseColumns,
        SelectedcolumnDefs: selected.length ? selected : this.baseColumns,
      });
    } else {
      this.setState({
        AllcolumnDefs: this.baseColumns,
        SelectedcolumnDefs: this.baseColumns,
      });
    }

    // data load
    await this.fetchData(userInfo?._id, userInfo?.database);
  }

  // ---------------------------------------------
  // Data load
  // ---------------------------------------------
  fetchData = async (_id, db) => {
    try {
      this.setState({ Loading: true });
      const res = await _Get(Create_Warehouse_List, db);
      const value = res?.Warehouse || [];

      const alldata = value.flatMap((w) =>
        (w?.productItems || []).map((pi) => ({
          ...w,
          damage: pi,
        }))
      );

      const damage = alldata.filter((x) => !!x?.damage?.damageItem);

      this.setState({
        rowAllData: damage,
        rowData: damage, // date filter not applied yet
      });
    } catch (err) {
      console.error(err);
      this.setState({ rowAllData: [], rowData: [] });
    } finally {
      this.setState({ Loading: false });
    }
  };

  // ---------------------------------------------
  // UI handlers: search / dates / sorting / pagination
  // ---------------------------------------------
  updateSearchQuery = (val) => {
    this.setState({ searchText: val, currentPage: 1 });
  };

  handleDate = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value });
  };

  handleSubmitDate = () => {
    const { rowAllData, startDate, EndDate } = this.state;
    if (!startDate || !EndDate) {
      this.setState({ rowData: rowAllData, currentPage: 1 });
      return;
    }
    const filtered = rowAllData.filter((item) => {
      const base = item?.updatedAt || item?.createdAt;
      const only = toDateOnly(base);
      return only >= startDate && only <= EndDate;
    });
    this.setState({ rowData: filtered, currentPage: 1 });
  };

  setPageSize = (e) => {
    const size = Number(e.target.value || 15);
    this.setState({ paginationPageSize: size, currentPage: 1 });
  };

  setPage = (n) => {
    this.setState((s) => ({ currentPage: n }));
  };

  sortBy = (colId) => {
    this.setState((prev) => {
      const { sortConfig } = prev;
      let direction = "asc";
      if (sortConfig.key === colId && sortConfig.direction === "asc")
        direction = "desc";
      return { sortConfig: { key: colId, direction } };
    });
  };

  // ---------------------------------------------
  // Column chooser modal handlers
  // ---------------------------------------------
  LookupviewStart = () =>
    this.setState((p) => ({ modal: !p.modal, Arrindex: -1 }));

  handleChangeHeader = (e, value) => {
    if (e.target.checked) {
      SelectedColums.push(value);
    } else {
      const idx = SelectedColums.findIndex((x) => x.id === value.id);
      if (idx >= 0) SelectedColums.splice(idx, 1);
    }
  };

  HeadingRightShift = () => {
    const views = this.state.SelectedcolumnDefs.slice();
    const ids = new Set(views.map((c) => c.id));
    SelectedColums.forEach((c) => {
      if (!ids.has(c.id)) views.push(c);
    });
    this.setState({ SelectedcolumnDefs: views });
  };

  handleLeftShift = () => {
    const { Arrindex, SelectedcolumnDefs } = this.state;
    if (Arrindex < 0) return;
    const next = SelectedcolumnDefs.slice();
    next.splice(Arrindex, 1);
    this.setState({ SelectedcolumnDefs: next, Arrindex: -1 });
  };

  shiftElementUp = () => {
    const { Arrindex, SelectedcolumnDefs } = this.state;
    if (Arrindex > 0) {
      const next = SelectedcolumnDefs.slice();
      const el = next.splice(Arrindex, 1)[0];
      next.splice(Arrindex - 1, 0, el);
      this.setState({ SelectedcolumnDefs: next, Arrindex: Arrindex - 1 });
    }
  };

  shiftElementDown = () => {
    const { Arrindex, SelectedcolumnDefs } = this.state;
    if (Arrindex >= 0 && Arrindex < SelectedcolumnDefs.length - 1) {
      const next = SelectedcolumnDefs.slice();
      const el = next.splice(Arrindex, 1)[0];
      next.splice(Arrindex + 1, 0, el);
      this.setState({ SelectedcolumnDefs: next, Arrindex: Arrindex + 1 });
    }
  };

  HandleSetVisibleField = (e) => {
    e.preventDefault();
    // Persist visible column headers for next time (store by headerName)
    const toStore = this.state.SelectedcolumnDefs.map((c) => ({
      headerName: c.headerName,
    }));
    localStorage.setItem("closingStockReport", JSON.stringify(toStore));
    this.LookupviewStart();
  };

  // ---------------------------------------------
  // Downloaders (PDF / CSV / XLS) using visible cols + filtered rows
  // ---------------------------------------------
  getVisibleData = () => {
    const { SelectedcolumnDefs, rowData, searchText, sortConfig } = this.state;

    // Search
    const searched = (rowData || []).filter((row) => {
      const q = (searchText || "").toLowerCase();
      if (!q) return true;
      // check across main text columns
      const parts = [
        safe(row?.damage?.productId?.Product_Title),
        safe(row?.damage?.productId?.HSN_Code),
        safe(row?.warehouseName),
        toDateOnly(row?.createdAt || row?.updatedAt),
        String(row?.damage?.productId?.Product_MRP || ""),
      ];
      return parts.some((p) => String(p).toLowerCase().includes(q));
    });

    // Sort
    const toSort = [...searched];
    if (sortConfig?.key && sortConfig.key !== "UID") {
      const col =
        SelectedcolumnDefs.find((c) => c.id === sortConfig.key) ||
        this.baseColumns.find((c) => c.id === sortConfig.key);
      if (col && typeof col.getValue === "function") {
        const dir = sortConfig.direction === "desc" ? -1 : 1;
        toSort.sort((a, b) => {
          const av = col.getValue(a) ?? "";
          const bv = col.getValue(b) ?? "";
          if (typeof av === "number" && typeof bv === "number")
            return (av - bv) * dir;
          return String(av).localeCompare(String(bv)) * dir;
        });
      }
    }

    // to array of values (for export)
    const headers = SelectedcolumnDefs.map((c) => c.headerName);
    const rows = toSort.map((row, idx) =>
      SelectedcolumnDefs.map((col) =>
        col.id === "UID" ? idx + 1 : col.getValue(row, idx, idx)
      )
    );

    return { headers, rows, fullRows: toSort };
  };

  exportCSV = () => {
    const { headers, rows } = this.getVisibleData();
    const csv = Papa.unparse([headers, ...rows]);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "DamageStockReport.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  exportXLS = () => {
    const { headers, rows } = this.getVisibleData();
    const json = rows.map((r) => {
      const obj = {};
      headers.forEach((h, i) => (obj[h] = r[i]));
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(json);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DamageStockReport");
    XLSX.writeFile(wb, "DamageStockReport.xls");
  };

  exportPDF = () => {
    const { headers, rows } = this.getVisibleData();
    const doc = new jsPDF("landscape", "mm", "a4");
    doc.setTextColor(5, 87, 97);
    doc.setFontSize(12);
    doc.text("Damage Stock Report", 14, 14);
    doc.autoTable({
      head: [headers],
      body: rows,
      startY: 20,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [8, 91, 245] },
    });
    doc.save("DamageStockReport.pdf");
  };

  // ---------------------------------------------
  // Download dropdown hover
  // ---------------------------------------------
  toggleDropdown = (e) => {
    if (e.type === "mouseenter") {
      clearTimeout(this.hideTimeout);
      this.setState({ isOpen: true });
    } else if (e.type === "mouseleave") {
      this.hideTimeout = setTimeout(
        () => this.setState({ isOpen: false }),
        200
      );
    }
  };

  // ---------------------------------------------
  // Render helpers
  // ---------------------------------------------
  getPagedRows = (rows) => {
    const { paginationPageSize, currentPage } = this.state;
    const start = (currentPage - 1) * paginationPageSize;
    const end = start + paginationPageSize;
    return rows.slice(start, end);
  };

  render() {
    const {
      Loading,
      MasterShow,
      InsiderPermissions,
      SelectedcolumnDefs,
      AllcolumnDefs,
      isOpen,
      paginationPageSize,
      currentPage,
      searchText,
      sortConfig,
    } = this.state;

    if (Loading) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "20rem",
          }}
        >
          <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
            Loading...
          </Spinner>
        </div>
      );
    }

    const { headers, rows, fullRows } = this.getVisibleData();
    const totalPages = Math.max(
      1,
      Math.ceil(fullRows.length / paginationPageSize)
    );
    const pageRows = this.getPagedRows(fullRows);

    return (
      <>
        <a href="/#/dashboard" style={{ color: "green", paddingLeft: "10px" }}>
          Home
        </a>{" "}
        / Lead
        <Card className="card-color">
          <Row
            style={{ marginLeft: "3px", marginRight: "3px" }}
            className="card-color"
          >
            <Col>
              <h3
                className="float-left"
                style={{
                  fontWeight: 600,
                  textTransform: "uppercase",
                  fontSize: "18px",
                  marginTop: "30px",
                }}
              >
                Damage Stock Report
              </h3>
            </Col>

            {MasterShow ? (
              <Col lg="2" md="2" xl="2" style={{ marginTop: "25px" }}>
                <SuperAdminUI
                  onDropdownChange={(v) =>
                    localStorage.setItem(
                      "SuperadminIdByMaster",
                      JSON.stringify(v)
                    )
                  }
                  onSubmit={() => {
                    let SuperAdmin = JSON.parse(
                      localStorage.getItem("SuperadminIdByMaster")
                    );
                    if (!SuperAdmin) return;
                    let id = SuperAdmin.split(" ")[0];
                    let db = SuperAdmin.split(" ")[1];
                    this.fetchData(id, db);
                  }}
                />
              </Col>
            ) : (
              <Col />
            )}

            <Col style={{ marginTop: "25px" }} xl="3" lg="3" md="2">
              <div className="table-input cssforproductlist">
                <Input
                  className="input-color"
                  placeholder="search item here..."
                  onChange={(e) => this.updateSearchQuery(e.target.value)}
                  value={searchText}
                />
              </div>
            </Col>

            <Col xl="4" lg="4" md="5">
              <Row>
                <Col xl="5" lg="5" md="5" style={{ marginTop: "5px" }}>
                  <div className="table-input cssforproductlist">
                    <Label>Start Date</Label>
                    <Input
                      className="input-color"
                      type="date"
                      name="startDate"
                      value={this.state.startDate}
                      onChange={this.handleDate}
                    />
                  </div>
                </Col>
                <Col xl="5" lg="5" md="5" style={{ marginTop: "5px" }}>
                  <div className="table-input cssforproductlist">
                    <Label>End Date</Label>
                    <Input
                      className="input-color"
                      type="date"
                      name="EndDate"
                      value={this.state.EndDate}
                      onChange={this.handleDate}
                    />
                  </div>
                </Col>
                <Col xl="2" lg="2" md="2" style={{ marginTop: "25px" }}>
                  <div className="table-input">
                    <Button
                      type="button"
                      style={{
                        cursor: "pointer",
                        backgroundColor: "rgb(8, 91, 245)",
                        color: "white",
                        fontWeight: 600,
                        height: "43px",
                      }}
                      onClick={this.handleSubmitDate}
                    >
                      Submit
                    </Button>
                  </div>
                </Col>
              </Row>
            </Col>

            <Col lg="1" style={{ marginTop: "25px" }}>
              {InsiderPermissions?.View && (
                <span>
                  <FaFilter
                    style={{ cursor: "pointer" }}
                    title="filter column"
                    size="20px"
                    onClick={this.LookupviewStart}
                    color="rgb(8, 91, 245)"
                    className="float-right"
                  />
                </span>
              )}

              {InsiderPermissions?.Download && (
                <span
                  onMouseEnter={this.toggleDropdown}
                  onMouseLeave={this.toggleDropdown}
                >
                  <div
                    className="dropdown-container float-right"
                    style={{ position: "relative" }}
                  >
                    <ImDownload
                      style={{ cursor: "pointer" }}
                      title="Download file"
                      size="20px"
                      className="dropdown-button"
                      color="rgb(8, 91, 245)"
                    />
                    {isOpen && (
                      <div
                        style={{
                          position: "absolute",
                          zIndex: 10,
                          border: "1px solid rgb(8, 91, 245)",
                          backgroundColor: "white",
                          right: 0,
                        }}
                        className="dropdown-content dropdownmy"
                      >
                        <h5
                          onClick={this.exportPDF}
                          style={{ cursor: "pointer" }}
                          className="mx-1 myactive mt-1"
                        >
                          .PDF
                        </h5>
                        <h5
                          onClick={this.exportCSV}
                          style={{ cursor: "pointer" }}
                          className="mx-1 myactive"
                        >
                          .CSV
                        </h5>
                        <h5
                          onClick={this.exportXLS}
                          style={{ cursor: "pointer" }}
                          className="mx-1 myactive"
                        >
                          .XLS
                        </h5>
                      </div>
                    )}
                  </div>
                </span>
              )}
            </Col>
          </Row>

          <CardBody style={{ marginTop: "1rem" }}>
            {/* Page size + pagination */}
            <Row className="align-items-center mb-2">
              <Col sm="6">
                <div className="d-flex align-items-center gap-2">
                  <Label className="mb-0 me-2">Page size</Label>
                  <Input
                    type="select"
                    value={paginationPageSize}
                    onChange={this.setPageSize}
                    style={{ width: 90 }}
                  >
                    {[10, 15, 25, 50, 100].map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Input>
                  <div className="ms-3">
                    <small>
                      Showing {pageRows.length} of {fullRows.length} rows
                    </small>
                  </div>
                </div>
              </Col>
              <Col sm="6" className="d-flex justify-content-end">
                <div className="d-flex align-items-center gap-2">
                  <Button
                    size="sm"
                    disabled={currentPage <= 1}
                    onClick={() => this.setPage(currentPage - 1)}
                  >
                    Prev
                  </Button>
                  <span style={{ minWidth: 80, textAlign: "center" }}>
                    {currentPage} / {totalPages}
                  </span>
                  <Button
                    size="sm"
                    disabled={currentPage >= totalPages}
                    onClick={() => this.setPage(currentPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              </Col>
            </Row>

            {/* Data table */}
            <div style={{ overflowX: "auto" }}>
              <Table bordered responsive hover size="sm">
                <thead>
                  <tr>
                    {SelectedcolumnDefs.map((c) => {
                      const sortable = c.id !== "UID";
                      const isActive = sortConfig.key === c.id;
                      const arrow =
                        isActive && sortable
                          ? sortConfig.direction === "asc"
                            ? " ▲"
                            : " ▼"
                          : "";
                      return (
                        <th
                          key={c.id}
                          style={{
                            whiteSpace: "nowrap",
                            cursor: sortable ? "pointer" : "default",
                          }}
                          onClick={() => sortable && this.sortBy(c.id)}
                          title={c.headerName}
                        >
                          {c.headerName}
                          {arrow}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row, localIdx) => {
                    const absoluteIndex =
                      (this.state.currentPage - 1) *
                        this.state.paginationPageSize +
                      localIdx;
                    return (
                      <tr key={absoluteIndex}>
                        {SelectedcolumnDefs.map((c) => {
                          const val =
                            c.id === "UID"
                              ? absoluteIndex + 1
                              : c.getValue(row, localIdx, absoluteIndex);
                          return (
                            <td key={c.id} style={{ whiteSpace: "nowrap" }}>
                              {typeof val === "number" ? val : String(val)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                  {pageRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={SelectedcolumnDefs.length}
                        className="text-center"
                      >
                        No records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
        {/* Column Chooser Modal */}
        <Modal
          isOpen={this.state.modal}
          toggle={this.LookupviewStart}
          style={{ maxWidth: 1050 }}
        >
          <ModalHeader toggle={this.LookupviewStart}>Change Fields</ModalHeader>
          <ModalBody className="modalbodyhead">
            <Row>
              <Col lg="4" md="4" sm="12" xl="4" xs="12">
                <h5>Available Columns</h5>
                <div
                  style={{
                    maxHeight: 360,
                    overflowY: "auto",
                    border: "1px solid #ddd",
                    padding: 8,
                  }}
                >
                  {AllcolumnDefs.map((ele, i) => (
                    <div
                      key={ele.id}
                      className="mt-1 d-flex align-items-center"
                    >
                      <Input
                        type="checkbox"
                        className="me-2"
                        onChange={(e) => this.handleChangeHeader(e, ele, i)}
                      />
                      <span>{ele.headerName}</span>
                    </div>
                  ))}
                </div>
              </Col>

              <Col
                lg="2"
                md="2"
                sm="12"
                xl="2"
                xs="12"
                className="d-flex flex-column align-items-center"
              >
                <div className="mt-4">
                  <FaArrowAltCircleRight
                    onClick={this.HeadingRightShift}
                    style={{ cursor: "pointer" }}
                    size="30px"
                  />
                </div>
                <div className="mt-3">
                  <FaArrowAltCircleLeft
                    onClick={this.handleLeftShift}
                    style={{ cursor: "pointer" }}
                    size="30px"
                  />
                </div>
              </Col>

              <Col lg="6" md="6" sm="12" xl="6" xs="12">
                <Row>
                  <Col lg="8" md="8" sm="12" xs="12">
                    <h5>Visible Columns</h5>
                    <div
                      style={{
                        maxHeight: 360,
                        overflowY: "auto",
                        border: "1px solid #ddd",
                        padding: 8,
                      }}
                    >
                      {SelectedcolumnDefs.map((ele, i) => (
                        <div key={ele.id} className="mt-1">
                          <h6
                            onClick={() => this.setState({ Arrindex: i })}
                            style={{
                              cursor: "pointer",
                              padding: "6px 8px",
                              borderRadius: 6,
                              backgroundColor:
                                this.state.Arrindex === i
                                  ? "#e7f0ff"
                                  : "#f8f9fa",
                              border:
                                this.state.Arrindex === i
                                  ? "1px solid #7aa7ff"
                                  : "1px solid #e1e1e1",
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <IoMdRemoveCircleOutline
                              onClick={() => {
                                const next = SelectedcolumnDefs.slice();
                                const delindex = next.findIndex(
                                  (x) => x.id === ele.id
                                );
                                if (delindex >= 0) next.splice(delindex, 1);
                                this.setState({ SelectedcolumnDefs: next });
                              }}
                              style={{ cursor: "pointer" }}
                              size="20px"
                              color="red"
                              className="me-1"
                              title="Remove"
                            />
                            {ele.headerName}
                          </h6>
                        </div>
                      ))}
                    </div>
                  </Col>

                  <Col
                    lg="4"
                    md="4"
                    sm="12"
                    xs="12"
                    className="d-flex flex-column align-items-center"
                  >
                    <div className="mt-4">
                      <BsFillArrowUpSquareFill
                        size="30px"
                        onClick={this.shiftElementUp}
                        style={{ cursor: "pointer" }}
                        title="Move Up"
                      />
                    </div>
                    <div className="mt-3">
                      <BsFillArrowDownSquareFill
                        size="30px"
                        onClick={this.shiftElementDown}
                        style={{ cursor: "pointer" }}
                        title="Move Down"
                      />
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>

            <Row className="mt-2">
              <Col>
                <div className="d-flex justify-content-center">
                  <Badge
                    style={{ cursor: "pointer" }}
                    color="primary"
                    onClick={this.HandleSetVisibleField}
                  >
                    Submit
                  </Badge>
                </div>
              </Col>
            </Row>
          </ModalBody>
        </Modal>
      </>
    );
  }
}

export default DamageStockReport;
